import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import IrisPage from 'yoda-site-components/lib/components/IrisPage/IrisPage';
import irisComponentsForHome from 'yoda-site-components/lib/iris/components/forHome';
import ScrollTop from 'yoda-site-components/lib/components/ScrollTop/ScrollTop';
import SeoSchema from 'yoda-site-components/lib/components/SeoSchema/SeoSchema';
import LocalStorage from 'yoda-core-components/lib/helpers/LocalStorage/LocalStorage';
import { hotdealsPostProcessor } from 'yoda-interfaces/lib/IrisPage/IrisPage';
import _get from 'lodash/get';
import { selectDeviceName } from 'yoda-site-components/lib/selectors/ContextSelector';
import { isEmpty } from 'lodash';
import { getCriteoData } from 'yoda-site-components/lib/actions/CriteoAction';
import renderMiniPDP from '../../../helper/MiniPDPHelper';
import { onPageReady } from '../../../actions/HomePageAction';
import {
    isMktFallbackRedirectEnabled,
    marketingFallbackUrl,
    url,
    ttl,
    version,
    root,
    HelpSection,
    ConnectWithUs,
} from '../../../globalConstants';

const defaultPreferences = {
    renderingStrategy: {
        home: {
            limitInitialComponentsRendered: { mobile: 8, desktop: 9, tablet: 8 },
            offset: { mobile: 500, desktop: 800, tablet: 500 },
        },
    },
    enableHomeSeoSchema: true,
    isMktFallbackRedirectEnabled,
    marketingFallbackUrl,
    url,
    ttl,
    version,
    root,
};

const buildHomeSeoSchema = (connectWithUs, helpSection, logoUrlLink) => {
    const connectWithUsLinks = (connectWithUs || []).map((i) => i.linkPath);
    const contactPhoneNumber =
        (helpSection || []).find((i) => i.id === 'give_call')?.textStrong || '';

    return [
        {
            '@context': 'https://schema.org',
            '@graph': [
                {
                    '@type': 'Organization',
                    url: 'https://www.jcpenney.com/',
                    name: 'JCPenney',
                    alternateName: ['JCP', "Penney's", 'Penney OpCo LLC'],
                    logo: { '@type': 'ImageObject', url: logoUrlLink },
                    sameAs: connectWithUsLinks,
                    contactPoint: {
                        '@type': 'ContactPoint',
                        telephone: contactPhoneNumber,
                        contactType: 'Customer Support',
                    },
                },
            ],
        },
        {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            url: 'https://www.jcpenney.com/',
            potentialAction: {
                '@type': 'SearchAction',
                target: 'https://www.jcpenney.com/s?searchTerm={search_term_string}',
                'query-input': 'required name=search_term_string',
            },
        },
    ];
};

/* istanbul ignore next */
function HomePage({
    actions,
    irisData,
    location,
    isPreview,
    irisFallbackEnabled,
    irisFallbackPreferences,
    disableIrisCall,
    connectWithUs,
    helpSection,
    logoUrlLink,
    enableHomeSeoSchema,
    history,
    limitInitialComponentsRendered,
    offset,
    deviceName,
    enableMiniPDPFeature,
    enableCriteo,
}) {
    const prevIrisDataRef = useRef(irisData);

    // componentDidMount equivalent
    useEffect(() => {
        if (!isEmpty(irisData)) {
            const { analytics } = irisData;
            if (analytics) {
                actions.onPageReady(analytics.pageName, analytics.pageType);
            }
        }

        LocalStorage.removeData('scrollToCartridge');
        LocalStorage.removeData('galleryScrollTo');

        if (!__SERVER__ && enableCriteo) {
            actions.getCriteoData({ pageType: 'Home' });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // componentDidUpdate equivalent — fire analytics when irisData changes
    useEffect(() => {
        if (prevIrisDataRef.current !== irisData && !isEmpty(irisData)) {
            const { analytics } = irisData;
            if (analytics) {
                actions.onPageReady(analytics.pageName, analytics.pageType);
            }
        }
        prevIrisDataRef.current = irisData;
    }, [irisData, actions]);

    // === IRIS ===
    let params = '/home';
    if (isPreview) {
        params = `/home${location.search}`;
    }

    return (
        <>
            <ScrollTop />
            {enableHomeSeoSchema && (
                <SeoSchema
                    isInHeadTag
                    schema={buildHomeSeoSchema(connectWithUs, helpSection, logoUrlLink)}
                />
            )}
            <IrisPage
                params={params}
                pageType="HOME"
                aggregateAPI
                data-automation-id="home"
                postProcessor={hotdealsPostProcessor}
                limitInitialComponentsRendered={limitInitialComponentsRendered[deviceName]}
                offset={offset[deviceName]}
                location={location}
                history={history}
                irisFallbackEnabled={irisFallbackEnabled}
                disableIrisCall={disableIrisCall}
                irisFallbackPreferences={irisFallbackPreferences}
                irisComponents={irisComponentsForHome}
            />
            {enableMiniPDPFeature ? renderMiniPDP() : null}
        </>
    );
}

HomePage.propTypes = {
    actions: PropTypes.objectOf(PropTypes.func).isRequired,
    irisData: PropTypes.object,
    location: PropTypes.object,
    isPreview: PropTypes.bool,
    irisFallbackEnabled: PropTypes.bool,
    irisFallbackPreferences: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
    disableIrisCall: PropTypes.bool,
    connectWithUs: PropTypes.array,
    helpSection: PropTypes.array,
    logoUrlLink: PropTypes.string,
    enableHomeSeoSchema: PropTypes.bool,
    history: PropTypes.object,
    offset: PropTypes.object,
    deviceName: PropTypes.string,
    limitInitialComponentsRendered: PropTypes.object,
    enableMiniPDPFeature: PropTypes.bool,
    enableCriteo: PropTypes.bool,
};

HomePage.defaultProps = {
    irisData: {},
    location: {},
    isPreview: false,
    irisFallbackEnabled: false,
    disableIrisCall: false,
    connectWithUs: [],
    helpSection: [],
    logoUrlLink: '',
    enableHomeSeoSchema: true,
    history: {},
    offset: defaultPreferences.renderingStrategy.home.offset,
    limitInitialComponentsRendered:
        defaultPreferences.renderingStrategy.home.limitInitialComponentsRendered,
    deviceName: 'mobile',
    enableMiniPDPFeature: true,
    enableCriteo: false,
};

const mapStateToProps = ({ irisData, context }) => {
    const featureFlags = _get(context, 'featureFlags', {});
    const preferences = _get(context, 'preferences', {});

    return {
        irisData,
        isPreview: _get(context, 'isPreview', false),
        irisFallbackPreferences: _get(preferences, 'irisfallbackPreferences', defaultPreferences),
        irisFallbackEnabled: featureFlags.enableIrisFallback !== false,
        disableIrisCall: featureFlags.disableIrisCall || false,
        connectWithUs: _get(preferences, 'footer.ConnectWithUs', ConnectWithUs),
        helpSection: _get(preferences, 'footer.HelpSection', HelpSection),
        logoUrlLink: _get(preferences, 'assets.linkPath', ''),
        enableHomeSeoSchema: featureFlags.enableHomeSeoSchema !== false,
        limitInitialComponentsRendered: _get(
            preferences,
            'renderingStrategy.home.limitInitialComponentsRendered',
            defaultPreferences.renderingStrategy.home.limitInitialComponentsRendered
        ),
        offset: _get(
            preferences,
            'renderingStrategy.home.offset',
            defaultPreferences.renderingStrategy.home.offset
        ),
        deviceName: selectDeviceName({ context }),
        enableMiniPDPFeature: featureFlags.enableMiniPDPFeature !== false,
        enableCriteo: featureFlags.enableCriteo || false,
    };
};

const mapDispatchToProps = (dispatch) => ({
    actions: bindActionCreators({ onPageReady, getCriteoData }, dispatch),
});

export { HomePage };
export default connect(mapStateToProps, mapDispatchToProps)(HomePage);
